import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ROLE, checkRole } from '@common/components/RBAC';
import { useServiceRouter } from '@common/components/ServiceRouter';
import Tabs from '@common/components/Tabs';
import { IStudentProgramModel } from '@common/models/StudentPrograms/StudentProgram';

import ProgramCertificateButton from './CertificateButton';
import LessonsSection from './Section';
import * as Styled from './styled';

interface PropsType {
	programModel: IStudentProgramModel;
}

function ProgramLessons({ programModel }: PropsType) {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [loaded, setLoaded] = useState(false);
	const [page, setPage] = useState(1);
	const [tabIndex, setTabIndex] = useState(1);
	const { checkService } = useServiceRouter();
	const isTalented = checkService('talented');
	const isAcademy = checkService('academy');
	const isLyceum = checkService('lyceum');
	const isLearningCenter = checkService('learningCenter');
	const isDemoUser = checkRole(ROLE.DEMO_TIS);
	const isPartner = checkRole(ROLE.PARTNER);

	// const showCertificateButton =
	// 	(programModel.schedulePrograms?.is_self_study && !isDemoUser && !isLearningCenter) ||
	// 	isTalented;

	useEffect(() => {
		const currentPage = searchParams.get('page') || 1;
		const currentTab = searchParams.get('tab') || (isDemoUser || isTalented ? 0 : 1);

		setPage(+currentPage);
		setTabIndex(+currentTab);
		setLoaded(true);
	}, [searchParams]);

	const handleChangeTab = (value: number) => {
		const newParams = new URLSearchParams(searchParams);
		newParams.set('page', '1');
		newParams.set('tab', `${value}`);
		setSearchParams(newParams);
		setPage(1);
	};

	const handleChangePage = (value: number) => {
		const newParams = new URLSearchParams(searchParams);
		newParams.set('page', `${value}`);
		setSearchParams(newParams);
		setPage(value);
	};

	const canShowCertificateButton =
		[programModel.schedulePrograms?.is_self_study, isTalented].includes(true) &&
		![isDemoUser, isPartner, isLearningCenter].includes(true) && !isTalented;

	return (
		loaded && (
			<Styled.Wrap>
				<Tabs
					{...(canShowCertificateButton
						? {
								header: (
									<ProgramCertificateButton programModel={programModel} isTalented={isTalented} />
								),
							}
						: {})}
					value={tabIndex}
					tabs={[
						...(!isDemoUser
							? [
									{
										label: t('tabs.allLessons'),
										element: (
											<LessonsSection
												programModel={programModel}
												lessonsKey="all_lessons"
												page={page}
												setPage={handleChangePage}
											/>
										),
										badge: programModel?.lessons.all_lessons?.length,
									},
								]
							: []),
						{
							label: [isAcademy, isLyceum].includes(true) ? t('tabs.todo') : t('tabs.active'),
							element: (
								<LessonsSection
									programModel={programModel}
									lessonsKey="active"
									page={page}
									setPage={handleChangePage}
								/>
							),
							badge: programModel?.lessons.active?.length,
						},
						...([isAcademy].includes(true)
							? [
									{
										label: t('tabs.renew'),
										element: (
											<LessonsSection
												programModel={programModel}
												lessonsKey="reset"
												page={page}
												setPage={handleChangePage}
											/>
										),
										badge: programModel?.lessons.reset?.length,
									},
								]
							: []),
						...(![isDemoUser, isPartner].includes(true)
							? [
									{
										label: t('tabs.onChecking'),
										element: (
											<LessonsSection
												programModel={programModel}
												lessonsKey="on_checking"
												page={page}
												setPage={handleChangePage}
											/>
										),
										badge: programModel?.lessons.on_checking?.length,
									},
								]
							: []),
						{
							label: t('tabs.completed'),
							element: (
								<LessonsSection
									programModel={programModel}
									lessonsKey="completed"
									page={page}
									setPage={handleChangePage}
								/>
							),
							badge: programModel?.lessons.completed?.length,
						},
					]}
					variant="scrollable"
					onChange={handleChangeTab}
				/>
			</Styled.Wrap>
		)
	);
}

export default ProgramLessons;
